import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFindingDetails } from "./FindingDetailsProvider";
import _ from "lodash";
import FindingAPI from "restAPI/FindingAPI";
import { PermissionSetMap } from "constants/RBACConstants";
import {
  FINDING_ACTIONS,
  FINDING_STATUS,
  PROPOSE_MITIGATED_SUPPORTED_TOOLS,
} from "../Constants";
import Vocabulary from "components/Vocabulary";
import { Dropdown, Menu, Tooltip } from "antd";
import { hasPermission } from "utils/authService";
import { isCustomFinding, showResurfaceTimeForAcceptRisk } from "../utils";
import { LoadingIcon } from "components/Icons";
import { DownOutlined } from "@ant-design/icons";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";

export default function FindingStatusDropdown() {
  const { showFindingActionPopup, loading, bucket, history, finding } =
    useFindingDetails();
  const [acceptRiskData, setAcceptRiskData] = useState({});
  const { disabledFindingStatus } = useGlobalSettingsContext();

  useEffect(() => {
    if (!_.isUndefined(finding?.id)) {
      const { promise, cancel } = FindingAPI.getAcceptRiskCount(finding.id);
      promise.then((res) => {
        setAcceptRiskData({
          count: res.acceptRiskCount,
          maxCount: res.maxAcceptRiskCount,
        });
      });
      return cancel;
    }
  }, [finding]);

  const setFindingsAndRedirect = useCallback(
    (path) => {
      bucket.addFindings([finding.id], [finding]);
      history.push(path);
    },
    [bucket, history, finding]
  );

  const actions = useMemo(() => {
    if (loading) {
      return [];
    }
    const isAcceptRiskLimitReached =
      _.isNumber(acceptRiskData.maxCount) &&
      acceptRiskData.count >= acceptRiskData.maxCount;

    let action = [
      {
        key: "FALSEPOSITIVE",
        permission: PermissionSetMap.MARK_FALSE_POSITIVE,
        display: finding.status !== FINDING_STATUS.FALSE_POSITIVE,
        disabled: finding.status === FINDING_STATUS.FALSE_POSITIVE,
        content: <Vocabulary name="falsePositive" />,
      },
      {
        key: "ACCEPT_RISK_ACCEPT",
        permission: PermissionSetMap.MARK_ACCEPT_RISK,
        display: finding.status !== FINDING_STATUS.ACCEPT_RISK,
        content: (
          <Tooltip
            title={
              hasPermission(PermissionSetMap.MARK_ACCEPT_RISK) &&
              isAcceptRiskLimitReached
                ? `Accept risk cannot be applied for more than ${acceptRiskData.maxCount} times`
                : ""
            }
          >
            {" "}
            <Vocabulary name="acceptRisk" />{" "}
          </Tooltip>
        ),
        disabled:
          finding.status === FINDING_STATUS.ACCEPT_RISK ||
          isAcceptRiskLimitReached,
      },
      {
        key: "CONFIRMED",
        permission: PermissionSetMap.MARK_CONFIRM,
        display: finding.status !== FINDING_STATUS.CONFIRMED,
        disabled: finding.status === FINDING_STATUS.CONFIRMED,
        content: <Vocabulary name="confirmed" />,
      },
      {
        key: "MITIGATED",
        permission: PermissionSetMap.MARK_MITIGATED,
        display: finding.status !== FINDING_STATUS.MITIGATED,
        disabled:
          (!isCustomFinding(finding.testType) ||
            finding.status === FINDING_STATUS.MITIGATED) &&
          !PROPOSE_MITIGATED_SUPPORTED_TOOLS.includes(finding.testType),
        content: (
          <Tooltip
            title={
              !hasPermission(PermissionSetMap.MARK_MITIGATED) ? (
                "No access"
              ) : !isCustomFinding(finding.testType) &&
                !PROPOSE_MITIGATED_SUPPORTED_TOOLS.includes(
                  finding.testType
                ) ? (
                <>
                  <Vocabulary name="mitigated" /> not available for this finding
                </>
              ) : (
                <>
                  Selected finding is{" "}
                  {finding.status === FINDING_STATUS.MITIGATED ? (
                    <>
                      {" "}
                      already <Vocabulary name="mitigated" />
                    </>
                  ) : (
                    <>
                      open for <Vocabulary name="mitigated" />
                    </>
                  )}
                </>
              )
            }
          >
            {" "}
            <Vocabulary name="mitigated" />{" "}
          </Tooltip>
        ),
      },
      {
        key: "IN_PROGRESS",
        permission: PermissionSetMap.MARK_IN_PROGRESS,
        display: finding.status !== FINDING_STATUS.IN_PROGRESS && !disabledFindingStatus.includes(FINDING_STATUS.IN_PROGRESS),
        disabled: finding.status === FINDING_STATUS.IN_PROGRESS,
        content: <Vocabulary name="inProgressStatus" />,
      },
      {
        key: "TRIAGE",
        permission: PermissionSetMap.MARK_TRIAGE,
        display: finding.status !== FINDING_STATUS.TRIAGE && !disabledFindingStatus.includes(FINDING_STATUS.TRIAGE),
        disabled: finding.status === FINDING_STATUS.TRIAGE,
        content: <Vocabulary name="triageStatus" />,
      },
      {
          key: FINDING_ACTIONS.CONTROLLED,
          permission: PermissionSetMap.MARK_CONTROLLED,
          display: finding.status !== FINDING_STATUS.CONTROLLED && !disabledFindingStatus.includes(FINDING_STATUS.CONTROLLED),
          disabled: finding.status === FINDING_STATUS.CONTROLLED,
          content: <Vocabulary name="controlledStatus" />,
      },
      {
        key: "SUPPRESSED",
        permission: PermissionSetMap.MARK_SUPPRESSED,
        display: finding.status !== FINDING_STATUS.SUPPRESSED,
        disabled: finding.status === FINDING_STATUS.SUPPRESSED,
        content: <Vocabulary name="suppressed" />,
      },
      {
        key: "ACCEPT_RISK_REJECT",
        permission: PermissionSetMap.MARK_REOPEN,
        display:
          finding.status === FINDING_STATUS.ACCEPT_RISK ||
          finding.status === FINDING_STATUS.CONTROLLED,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "FALSE_POSITIVE_NOT",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.FALSE_POSITIVE,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "NOT_CONFIRMED",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.CONFIRMED,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "NOT_IN_PROGRESS",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.IN_PROGRESS,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "NOT_TRIAGE",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.TRIAGE,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "NOT_SUPPRESSED",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.SUPPRESSED,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
      {
        key: "NOT_MITIGATED",
        permission: PermissionSetMap.MARK_REOPEN,
        display: finding.status === FINDING_STATUS.MITIGATED,
        content: (
          <>
            Re-
            <Vocabulary name="open" />
          </>
        ),
      },
    ];

    return action.filter((a) => a.display !== false);
  }, [finding, acceptRiskData, loading, disabledFindingStatus]);

  const handleMenuClick = useCallback(
    (e) => {
      switch (e.key) {
        case "ACCEPT_RISK_REJECT":
          showFindingActionPopup(FINDING_ACTIONS.REJECT_RISK);
          break;
        case "ACCEPT_RISK_ACCEPT":
          showFindingActionPopup(FINDING_ACTIONS.ACCEPT_RISK);
          break;
        case "FALSE_POSITIVE_NOT":
          showFindingActionPopup(FINDING_ACTIONS.NOT_FALSEPOSITIVE);
          break;
        case "FALSEPOSITIVE":
          showFindingActionPopup(FINDING_ACTIONS.FALSEPOSITIVE);
          break;
        case "IN_PROGRESS":
          showFindingActionPopup(FINDING_ACTIONS.IN_PROGRESS);
          break;
        case "SUPPRESSED":
          showFindingActionPopup(FINDING_ACTIONS.SUPPRESSED);
          break;
        case "TRIAGE":
          showFindingActionPopup(FINDING_ACTIONS.TRIAGE);
          break;
        case "NOT_CONFIRMED":
          showFindingActionPopup(FINDING_ACTIONS.NOT_CONFIRMED);
          break;
        case "CONFIRMED":
          showFindingActionPopup(FINDING_ACTIONS.CONFIRMED);
          break;
        case "MITIGATED":
          setFindingsAndRedirect("/findings/mitigated");
          break;
        case "NOT_MITIGATED":
          showFindingActionPopup(FINDING_ACTIONS.NOT_CONFIRMED); //use NOT_CONFIRMED here because popup are same for all
          break;
        case "NOT_IN_PROGRESS":
          showFindingActionPopup(FINDING_ACTIONS.NOT_IN_PROGRESS);
          break;
        case "NOT_SUPPRESSED":
          showFindingActionPopup(FINDING_ACTIONS.NOT_SUPPRESSED);
          break;
        case "NOT_TRIAGE":
          showFindingActionPopup(FINDING_ACTIONS.NOT_TRIAGE);
          break;
        default:
          showFindingActionPopup(e.key);
          break;
      }
    },
    [showFindingActionPopup, setFindingsAndRedirect]
  );

  const menu = useMemo(() => {
    return (
      <Menu onClick={handleMenuClick}>
        {actions.map((item) => {
          const hasAccess = item.permission
            ? hasPermission(item.permission)
            : true;
          return (
            <Menu.Item
              key={item.key}
              disabled={!hasAccess || item.disabled}
              title={!hasAccess ? "No Access" : item.tooltip}
            >
              {item.content}
            </Menu.Item>
          );
        })}
      </Menu>
    );
  }, [actions, handleMenuClick]);

  if (loading) {
    return <LoadingIcon />;
  }
  return (
    <>
      <Dropdown size="small" type="text" overlay={menu} trigger="click">
        <div className="d-flex align-items-center">
          <span className="text-ellipsis m-r-xs">
            {showResurfaceTimeForAcceptRisk(finding || {})}
          </span>
          <DownOutlined />
        </div>
      </Dropdown>
    </>
  );
}
